import { cn } from "cn";
import type { ComponentProps } from "react";

/** The screen's one main action (accent), a second one (outline), one in a bar or a list (quiet), and the confirmation
 * of a deletion inside a dialog (destructive). */
export type ButtonVariant = "primary" | "outline" | "quiet" | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

const looks: Record<ButtonVariant, string> = {
  primary: "bg-accent text-white hover:bg-accent-hover",
  outline: "border-control-border bg-transparent text-graphite hover:border-graphite",
  quiet: "bg-transparent text-text hover:bg-surface",
  destructive: "bg-danger text-white hover:opacity-90",
};

const sizes: Record<ButtonSize, string> = { sm: "h-9 px-3", md: "h-10 px-4", lg: "h-11 px-8" };

type Look = { variant?: ButtonVariant | undefined; size?: ButtonSize | undefined; icon?: boolean | undefined };

/** The look of a button, for a link that leads where a button would act: a link keeps the middle click and the new
 * tab. Under a finger a button is never shorter than a fingertip. */
export function buttonClass({ variant = "primary", size = "md", icon = false }: Look = {}): string {
  return cn(
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-control border border-transparent text-sm leading-none font-medium whitespace-nowrap no-underline transition-colors duration-150 select-none pointer-coarse:min-h-target-touch",
    "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-[1.5]",
    looks[variant],
    sizes[size],
    icon && "aspect-square px-0 pointer-coarse:min-w-target-touch",
  );
}

export function Button({ variant, size, icon, type = "button", className, ...props }: ComponentProps<"button"> & Look) {
  return <button type={type} className={cn(buttonClass({ variant, size, icon }), className)} {...props} />;
}
